import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ExpertRejectionCodePipe,
  RejectionCodesMap,
} from '@techspert-io/experts';
import { MaterialModule } from '@techspert-io/shared';
import { ToastService } from '@techspert-io/user-alerts';
import { finalize, merge } from 'rxjs';
import { CurrencyService } from '../../../../shared/services/currency/currency.service';
import {
  CallSolicitationOutcomeMap,
  ICallSolicitation,
  ICallSolicitationUpdate,
  ICallSolicitationUpdateReturn,
} from '../../models/call-solicitations.models';
import { CallSolicitationOutcomePipe } from '../../pipes/call-solicitation-outcome.pipe';
import { CallSolicitationsService } from '../../services/call-solicitations.service';
import { isCallSolicitationRejectionOutcome } from '../../utils/call-solicitation-outcome.utils';

export interface ICallSolicitationOutcomeDialogData {
  solicitation: ICallSolicitation;
  expert: {
    expertId: string;
    profileId: string;
    name: string;
    primaryEmail?: string;
    primaryPhoneNumber: string;
    country?: string;
    linkedin?: string;
    latestJob?: {
      jobTitle: string;
      company: string;
      industry?: string;
    };
    honoraria?: {
      currency: string;
      qual?: number;
      quant?: number;
    };
  };
  opportunity: {
    opportunityId: string;
    opportunityName: string;
    projectType: string;
    anticipatedCallTime?: number;
  };
  segment?: {
    geography: string;
    segment: string;
  };
  pinnedNotes: string[];
}

type CallSolicitationOutcomeForm = FormGroup<{
  outcome: FormControl<CallSolicitationOutcomeMap | null>;
  rejectionCode: FormControl<RejectionCodesMap | null>;
  notes: FormControl<string>;
  saveNoteToProfile: FormControl<boolean>;
  honorariumQual: FormControl<number | null>;
  honorariumQuant: FormControl<number | null>;
  honorariumCurrency: FormControl<string | null>;
}>;

const CALL_SOLICITATION_OUTCOMES: readonly CallSolicitationOutcomeMap[] = [
  CallSolicitationOutcomeMap.Accepted,
  CallSolicitationOutcomeMap.ExpertRejection,
  CallSolicitationOutcomeMap.AdminRejection,
  CallSolicitationOutcomeMap.NoAnswer,
  CallSolicitationOutcomeMap.WrongNumber,
  CallSolicitationOutcomeMap.CallFailed,
];

const CALL_SOLICITATION_REJECTION_CODES: readonly RejectionCodesMap[] = [
  RejectionCodesMap.Unknown,
  RejectionCodesMap.Other,
  RejectionCodesMap.Duplicate,
  RejectionCodesMap.Mismatch,
  RejectionCodesMap.Money,
  RejectionCodesMap.Time,
  RejectionCodesMap.Compliance,
  RejectionCodesMap.Screener,
];

@Component({
  selector: 'app-call-solicitation-outcome-dialog',
  templateUrl: './call-solicitation-outcome-dialog.component.html',
  styleUrls: ['./call-solicitation-outcome-dialog.component.scss'],
  imports: [
    MaterialModule,
    CallSolicitationOutcomePipe,
    ExpertRejectionCodePipe,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallSolicitationOutcomeDialogComponent {
  private readonly dialogRef = inject(
    MatDialogRef<
      CallSolicitationOutcomeDialogComponent,
      ICallSolicitationUpdateReturn
    >
  );
  private readonly callSolicitationsService = inject(CallSolicitationsService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly data = inject<ICallSolicitationOutcomeDialogData>(MAT_DIALOG_DATA);
  readonly outcomeOptions = CALL_SOLICITATION_OUTCOMES;
  readonly rejectionCodeOptions = CALL_SOLICITATION_REJECTION_CODES;
  readonly currencies = inject(CurrencyService).currencyCodes;
  readonly isSaving = signal(false);

  readonly form: CallSolicitationOutcomeForm = new FormGroup({
    outcome: new FormControl<CallSolicitationOutcomeMap | null>(
      this.data.solicitation.outcome,
      Validators.required
    ),
    rejectionCode: new FormControl<RejectionCodesMap | null>(null),
    notes: new FormControl(this.data.solicitation.notes ?? '', {
      nonNullable: true,
    }),
    saveNoteToProfile: new FormControl(false, { nonNullable: true }),
    honorariumQual: new FormControl<number | null>(
      this.data.expert.honoraria?.qual ?? null,
      Validators.min(0)
    ),
    honorariumQuant: new FormControl<number | null>(
      this.data.expert.honoraria?.quant ?? null,
      Validators.min(0)
    ),
    honorariumCurrency: new FormControl<string | null>(
      this.data.expert.honoraria?.currency ?? null
    ),
  });

  constructor() {
    this.updateRejectionCodeValidators(this.form.controls.outcome.value);
    this.form.controls.outcome.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((outcome) => this.updateRejectionCodeValidators(outcome));

    merge(
      this.form.controls.honorariumQual.valueChanges,
      this.form.controls.honorariumQuant.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateHonorariumCurrencyValidators());
  }

  isRejectionOutcome(
    outcome: CallSolicitationOutcomeMap | null | undefined
  ): boolean {
    return isCallSolicitationRejectionOutcome(outcome);
  }

  save(): void {
    if (this.isSaving()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const {
      outcome,
      notes,
      rejectionCode,
      saveNoteToProfile,
      honorariumQual,
      honorariumQuant,
      honorariumCurrency,
    } = this.form.getRawValue();
    if (!outcome) {
      return;
    }

    const isRejectionOutcome = this.isRejectionOutcome(outcome);
    const request: ICallSolicitationUpdate = {
      call_solicitation_id: this.data.solicitation.call_solicitation_id,
      outcome,
      notes: notes.trim(),
      save_note_to_profile: saveNoteToProfile,
    };
    if (isRejectionOutcome && rejectionCode) {
      request.rejection_code = rejectionCode;
    }

    const honoraria = this.data.expert.honoraria;
    if (
      honoraria &&
      honorariumCurrency !== null &&
      (honorariumQual !== (honoraria.qual ?? null) ||
        honorariumQuant !== (honoraria.quant ?? null) ||
        honorariumCurrency !== honoraria.currency)
    ) {
      request.honorarium = {
        currency: honorariumCurrency,
        ...(honorariumQual !== null ? { qual: honorariumQual } : {}),
        ...(honorariumQuant !== null ? { quant: honorariumQuant } : {}),
      };
    }

    this.isSaving.set(true);

    this.callSolicitationsService
      .updateCallSolicitation(
        this.data.solicitation.call_solicitation_id,
        request
      )
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result) => {
          this.toastService.sendMessage('Call outcome saved', 'success');
          this.dialogRef.close(result);
        },
        error: () => {
          this.toastService.sendMessage('Failed to save call outcome', 'error');
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  private updateHonorariumCurrencyValidators(): void {
    const { honorariumQual, honorariumQuant, honorariumCurrency } =
      this.form.controls;

    if (honorariumQual.value !== null || honorariumQuant.value !== null) {
      honorariumCurrency.setValidators(Validators.required);
    } else {
      honorariumCurrency.clearValidators();
    }

    honorariumCurrency.updateValueAndValidity({ emitEvent: false });
  }

  private updateRejectionCodeValidators(
    outcome: CallSolicitationOutcomeMap | null
  ): void {
    const rejectionCodeControl = this.form.controls.rejectionCode;

    if (this.isRejectionOutcome(outcome)) {
      rejectionCodeControl.setValidators(Validators.required);
    } else {
      rejectionCodeControl.setValue(null, { emitEvent: false });
      rejectionCodeControl.clearValidators();
    }

    rejectionCodeControl.updateValueAndValidity({ emitEvent: false });
  }
}
